import { generateID, IOptionsWithTypes } from '@eventr/common'
import { MainButton } from '@eventr/common-storybook'
import { useMemo } from 'react'
import { useFormContext } from 'react-hook-form'
import { useShallow } from 'zustand/react/shallow'
import i18n from '../../../i18n/i18next'
import { bs, useBoundStore } from '../../../state/bound.store'
interface IFormJoinLeaveButtonProps {
    isJoined: boolean
    joinText?: string
    leaveText?: string
    keyToUsers: string
    sectionId: number
}

export const FormJoinLeaveButton = ({
    isJoined,
    joinText,
    leaveText,
    keyToUsers,
    sectionId,
}: IFormJoinLeaveButtonProps) => {
    const { selectedItinID } = useBoundStore(
        useShallow((s) => {
            return {
                selectedItinID: s.selectedItinID,
            }
        })
    )
    const { getValues, setValue } = useFormContext()

    // Get current user's ID from the store
    const currentUserId = useMemo(() => {
        // Get the current user from wherever it's stored in your app
        return bs().wpUser?.id || ''
    }, [])
    const _joinText = useMemo(() => {
        if (joinText) {
            return joinText
        } else {
            return i18n.t('edit-itinerary.joinButtonText.default')
        }
    }, [joinText])

    const _leaveText = useMemo(() => {
        if (leaveText) {
            return leaveText
        } else {
            return i18n.t('edit-itinerary.leaveButtonText.default')
        }
    }, [leaveText])

    const handleLeave = () => {
        const attendeesList = getValues(keyToUsers) as any[]

        if (!attendeesList || !Array.isArray(attendeesList)) {
            console.warn('No attendees list found at path:', keyToUsers)
            return
        }

        // Create a new array to avoid mutating the original
        const updatedAttendeesList = attendeesList
            .map((attendee) => {
                // If this attendee has no users array, return it unchanged
                if (!attendee.users || !Array.isArray(attendee.users)) {
                    return attendee
                }

                // Filter out the current user from the users array
                const updatedUsers = attendee.users.filter(
                    (user: IOptionsWithTypes) => user.option !== currentUserId
                )

                // Return a new attendee object with updated users array
                return {
                    ...attendee,
                    users: updatedUsers,
                }
            })
            // Filter out any attendees that now have empty users arrays
            .filter((attendee) => attendee.users && attendee.users.length > 0)

        // Update the form value
        setValue(keyToUsers, updatedAttendeesList, {
            shouldDirty: true,
            shouldTouch: true,
            shouldValidate: true,
        })
    }

    const handleJoin = () => {
        const attendeesList = (getValues(keyToUsers) as any[]) || []

        if (!currentUserId) {
            console.warn('Cannot join: No current user found')
            return
        }

        // Check if user is already in any attendee group
        const isAlreadyInList = attendeesList.some(
            (attendee) =>
                attendee.users &&
                attendee.users.some(
                    (user: IOptionsWithTypes) => user.option === currentUserId
                )
        )

        if (isAlreadyInList) {
            console.warn('User is already in the attendees list')
            return
        }

        // Create a new user object to add
        const userToAdd = {
            option: currentUserId,
            type: 'User',
        }
        // Create a new attendees list with the user added
        const updatedAttendeesList = [
            ...attendeesList,
            {
                _id: generateID(selectedItinID, sectionId),
                users: [userToAdd],
            },
        ]

        // Update the form
        setValue(keyToUsers, updatedAttendeesList, {
            shouldDirty: true,
            shouldTouch: true,
            shouldValidate: true,
        })
    }

    return (
        <>
            {isJoined && (
                <MainButton
                    id={`leave-button`}
                    onClick={handleLeave}
                    icon="Subtract"
                >
                    {_leaveText}
                </MainButton>
            )}
            {!isJoined && (
                <MainButton id={`join-button`} onClick={handleJoin} icon="Add">
                    {_joinText}
                </MainButton>
            )}
        </>
    )
}
